import { InfoIcon, UserIcon } from '@phosphor-icons/react'
import { addDays, isFuture } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { ChangeReservationLocationRequest } from '@/api/reservations/change-reservation-location'
import { useGetAmenityLocations } from '@/api/reservations/get-amenity-locations'
import { ReservationDetails } from '@/api/reservations/get-reservation-details'
import { BadgeColorKeys } from '@/components/ui/badge'
import { Chip } from '@/components/ui/chip'
import { Select } from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { EMPTY_VALUE } from '@/constants'
import { DEFAULT_TZ } from '@/constants/date'
import { popupFactory } from '@/factories'
import { UserRoleEnum } from '@/models'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { selectUserRole } from '@/reducers/userDataReducer'
import { formatDateOnly, formatDateTimeInTimezone, formatMinutes } from '@/utils'
import { formatPhoneNumberDisplay } from '@/utils/phone-number'

import { MoveLocationConfirmationPopup } from '../move-location-confirmation-popup'
import { DetailsInfoItem } from '../reservation-details.types'
import { getTimeBlockedLocationIds, getUserInfo } from '../reservation-details.utils'
import * as S from '../reservations-details.styled'

interface Props {
  reservation?: ReservationDetails
  onLocationChange: (data: ChangeReservationLocationRequest) => Promise<unknown>
  isLoading?: boolean
}

const formatReservationSlot = ({
  date,
  minutes,
  timezone,
}: {
  date?: number | null
  minutes?: number
  timezone: string
}) => {
  if (date == null || minutes == null) return EMPTY_VALUE

  // endTime 1440 means midnight at the end of the day, so it belongs to the next day
  const MINUTES_IN_DAY = 24 * 60
  const day = addDays(toZonedTime(new Date(date), timezone), Math.floor(minutes / MINUTES_IN_DAY))

  return `${formatDateOnly(day)} ${formatMinutes(minutes)}`
}

export const Details = ({ reservation, onLocationChange, isLoading }: Props) => {
  const { t } = useTranslation(['reservations'])
  const activeBuilding = useSelector(selectActiveBuilding)
  const userRole = useSelector(selectUserRole)
  const timezone = activeBuilding?.timezone ?? DEFAULT_TZ

  const params = reservation?.amenityId.multiDayBooking
    ? { date: reservation.date, endDate: reservation.endDate || undefined }
    : {
        endTime: reservation?.endTime,
        startTime: reservation?.startTime,
        timestamp: reservation?.date,
      }

  const { data: amenityLocations, isLoading: isAmenityLocationLoading } = useGetAmenityLocations({
    amenityId: reservation?.amenityId.id,
    ...params,
  })

  const timeBlockedLocationIds = getTimeBlockedLocationIds({
    date: reservation?.date,
    endDate: reservation?.endDate,
    endTime: reservation?.endTime,
    managerialBooking: reservation?.amenityId.managerialBooking,
    startTime: reservation?.startTime,
    unavailableSlots: reservation?.amenityId?.unavailableSlots?.regular,
  })

  const amenityCapacity = reservation?.amenityId?.capacity || 0
  const spaceOptions = (amenityLocations || []).map((location) => ({
    disabled:
      reservation?.locationId?.name === location.name ||
      location.reservations >= amenityCapacity ||
      timeBlockedLocationIds.includes(location.id),
    label: location.name,
    value: location.id.toString(),
  }))

  const isMultiDate = Boolean(reservation?.endDate)
  const customer = reservation?.clientId
    ? {
        apartment: reservation.clientId.apartment,
        email: reservation.clientId.user.email,
        firstName: reservation.clientId.user.firstName,
        lastName: reservation.clientId.user.lastName,
        phone: reservation.clientId.phone,
      }
    : reservation?.guestId
      ? {
          email: reservation.guestId.email,
          firstName: reservation.guestId.firstName,
          lastName: reservation.guestId.lastName,
          phone: reservation.guestId.phone,
        }
      : undefined
  const customerType = reservation?.clientId
    ? { color: 'green', label: t('reservations:details.details.customerTypeResident') }
    : reservation?.guestId
      ? { color: 'orange', label: t('reservations:details.details.customerTypeGuest') }
      : undefined

  const hasAvailableSpaces =
    (amenityLocations || []).filter((location) => location.reservations < amenityCapacity).length >
      0 && spaceOptions.filter((option) => !option.disabled).length > 0

  const startsInFuture =
    reservation?.date != null && isFuture(reservation.date + (reservation.startTime ?? 0) * 60_000)

  const isManagerOrAdmin = [
    UserRoleEnum.enum.Administrator,
    UserRoleEnum.enum.PropertyManager,
  ].includes(userRole)

  const canEditSpace =
    reservation?.locationId?.id &&
    amenityLocations?.length &&
    ['approved'].includes(reservation.status) &&
    startsInFuture &&
    hasAvailableSpaces &&
    isManagerOrAdmin

  const handleConfirmLocationChange = async (newLocationId: string) => {
    const selectedLocation = spaceOptions.find((option) => option.value === newLocationId)

    if (!reservation?.id || !selectedLocation) {
      return
    }
    await onLocationChange({
      newLocationId,
      newLocationName: selectedLocation.label,
      notifyEmail: false,
      reservationId: reservation?.id,
    })
  }

  const bookingInfoItems: DetailsInfoItem[] = [
    {
      label: t('reservations:details.details.reservationId'),
      value: reservation?.id || EMPTY_VALUE,
    },
    {
      label: t('reservations:details.details.startDateAndTime'),
      value: isMultiDate
        ? formatDateOnly(toZonedTime(new Date(reservation?.date!), timezone))
        : formatReservationSlot({
            date: reservation?.date,
            minutes: reservation?.startTime,
            timezone,
          }),
    },
    {
      label: t('reservations:details.details.endDateAndTime'),
      value: isMultiDate
        ? formatDateOnly(toZonedTime(new Date(reservation?.endDate!), timezone))
        : formatReservationSlot({
            date: reservation?.date,
            minutes: reservation?.endTime,
            timezone,
          }),
    },
    { label: t('reservations:details.details.amenityName'), value: reservation?.amenityId.name },
    {
      hidden: !reservation?.locationId,
      label: t('reservations:details.details.space'),
      value: canEditSpace ? (
        <Select
          size="small"
          options={spaceOptions}
          value={reservation?.locationId?.id.toString()!}
          disabled={isLoading || isAmenityLocationLoading}
          onChange={(value: string) => {
            popupFactory.open(MoveLocationConfirmationPopup, {
              oldLocationName: reservation?.locationId?.name,
              onConfirm: async () => {
                await handleConfirmLocationChange(value)
              },
            })
          }}
        />
      ) : (
        reservation?.locationId?.name
      ),
    },
    {
      label: t('reservations:details.details.createdOn'),
      value: reservation?.createdAt
        ? formatDateTimeInTimezone(reservation.createdAt, timezone)
        : EMPTY_VALUE,
    },
    {
      label: t('reservations:details.details.createdBy'),
      value: getUserInfo(reservation?.createdByUser) || EMPTY_VALUE,
    },
    {
      hidden: !reservation?.cancelledAt,
      label: t('reservations:details.details.cancelledOn'),
      value: reservation?.cancelledAt
        ? formatDateTimeInTimezone(reservation.cancelledAt, timezone)
        : EMPTY_VALUE,
    },
    {
      hidden: !reservation?.cancelledByUser,
      label: t('reservations:details.details.cancelledBy'),
      value: getUserInfo(reservation?.cancelledByUser) || EMPTY_VALUE,
    },
  ]

  const customerInfoItems: DetailsInfoItem[] = [
    {
      label: t('reservations:details.details.customerType'),
      value: customerType ? (
        <Chip
          variant="light"
          color={customerType.color as BadgeColorKeys}
          label={customerType.label}
          size="small"
        />
      ) : (
        EMPTY_VALUE
      ),
    },
    {
      label: t('reservations:details.details.firstName'),
      value: customer?.firstName ?? EMPTY_VALUE,
    },
    {
      label: t('reservations:details.details.lastName'),
      value: customer?.lastName ?? EMPTY_VALUE,
    },
    {
      hidden: !!reservation?.guestId,
      label: t('reservations:details.details.unitNumber'),
      value: customer?.apartment ?? EMPTY_VALUE,
    },
    {
      label: t('reservations:details.details.email'),
      value: customer?.email ? (
        <S.CustomerEmail href={`mailto:${customer.email}`}>{customer.email}</S.CustomerEmail>
      ) : (
        EMPTY_VALUE
      ),
    },
    {
      hidden: !customer?.phone,
      label: t('reservations:details.details.phone'),
      value: customer?.phone ? `+1 ${formatPhoneNumberDisplay(customer.phone)}` : EMPTY_VALUE,
    },
  ]

  return (
    <>
      <S.CompactCard>
        <S.BasicInfo>
          <InfoIcon />
          <Typography size="heading-small" weight="bold">
            {t('reservations:details.details.bookingInformation')}
          </Typography>
        </S.BasicInfo>
        <S.Table>
          {bookingInfoItems.map(({ label, value, hidden }) =>
            hidden ? null : (
              <S.Row key={label}>
                <S.Label>{label}</S.Label>
                <S.Value>{value}</S.Value>
              </S.Row>
            ),
          )}
        </S.Table>
      </S.CompactCard>

      <S.CompactCard>
        <S.BasicInfo>
          <UserIcon />
          <Typography size="heading-small" weight="bold">
            {t('reservations:details.details.customerInformation')}
          </Typography>
        </S.BasicInfo>

        <S.Table>
          {customerInfoItems.map(({ label, value, hidden }) =>
            hidden ? null : (
              <S.Row key={label}>
                <S.Label>{label}</S.Label>
                <S.Value>{value}</S.Value>
              </S.Row>
            ),
          )}
        </S.Table>
      </S.CompactCard>
    </>
  )
}
